import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Drawer, Divider, Space } from 'antd';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import { Link, Outlet, useLocation } from 'react-router-dom';
// Import des icônes modernes pour améliorer l'interface
import { 
  HomeIcon, 
  SparklesIcon, 
  CurrencyDollarIcon, 
  InformationCircleIcon, 
  ChatBubbleLeftRightIcon,
  ChevronRightIcon 
} from '@heroicons/react/24/outline';
import logo from '../../assets/logo.png';

const { Header, Content, Footer } = Layout;

const PublicLayout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false); // État pour gérer la transparence du header
  const location = useLocation();
  
  // Navigation avec icônes modernes
  const navItems = [
    { key: '/', label: 'Accueil', path: '/', icon: HomeIcon },
    { key: '/features', label: 'Fonctionnalités', path: '/features', icon: SparklesIcon },
    { key: '/pricing', label: 'Tarifs', path: '/pricing', icon: CurrencyDollarIcon },
    { key: '/about', label: 'À propos', path: '/about', icon: InformationCircleIcon },
    { key: '/contact', label: 'Contact', path: '/contact', icon: ChatBubbleLeftRightIcon },
  ];

  // Gérer l'effet de scroll pour le header
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setScrolled(offset > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Détecter quelle page est active
  const getSelectedKeys = () => {
    const pathname = location.pathname;
    const item = navItems.find(item => item.path === pathname);
    return item ? [item.key] : ['/'];
  };

  return (
    <Layout className="min-h-screen">
      {/* Header moderne avec effet de transparence dynamique */}
      <Header 
        className={`
          ${scrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100' 
            : 'bg-white/90 backdrop-blur-sm shadow-md'
          }
          px-4 lg:px-8 h-20 flex items-center justify-between fixed w-full z-50 transition-all duration-300
        `}
      >
        <div className="flex items-center">
          {/* Logo et branding améliorés */}
          <Link to="/" className="flex items-center mr-8 group">
            <div className="relative">
              <img 
                src={logo}
                alt="Mercuri Shop Logo" 
                className="h-10 mr-3 transition-transform duration-300 group-hover:scale-110" 
                style={{ objectFit: 'contain' }}
              />
              {/* Effet de glow sur le logo */}
              <div className="absolute inset-0 bg-blue-400 rounded-full blur-lg opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
            </div>
            <div className="flex flex-col">
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                Mercuri Shop
              </span>
              <span className="text-xs text-gray-500 -mt-1">WhatsApp Business Solution</span>
            </div>
          </Link>
          
          {/* Menu desktop moderne */}
          <div className="hidden lg:block">
            <nav className="flex items-center space-x-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = getSelectedKeys().includes(item.key);
                return (
                  <Link 
                    key={item.key}
                    to={item.path}
                    className={`
                      flex items-center px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200
                      ${isActive 
                        ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
                      }
                    `}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
        
        {/* Actions desktop modernes */}
        <div className="hidden lg:flex items-center space-x-3">
          <Link to="/login">
            <Button 
              type="text" 
              className="h-10 px-6 text-gray-700 hover:text-blue-600 hover:bg-blue-50 border-0 rounded-xl font-medium transition-all duration-200"
            >
              Se connecter
            </Button>
          </Link>
          <Link to="/register">
            <Button 
              className="h-10 px-6 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 border-0 rounded-xl text-white font-medium shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transform hover:scale-105 transition-all duration-200"
            >
              S'inscrire gratuitement
              <ChevronRightIcon className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
        
        {/* Toggle menu mobile moderne */}
        <div className="lg:hidden">
          <Button 
            icon={<MenuOutlined className="text-lg" />} 
            onClick={toggleMobileMenu} 
            className="h-10 w-10 border-0 shadow-none bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200"
          />
        </div>
      </Header>
      
      {/* Drawer menu mobile moderne */}
      <Drawer
        placement="right"
        closable={false}
        onClose={closeMobileMenu}
        open={mobileMenuOpen}
        width="85%"
        bodyStyle={{ padding: 0 }}
        className="lg:hidden"
        style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }}
      >
        {/* En-tête du menu mobile */}
        <div className="p-6 bg-gradient-to-r from-blue-50 to-cyan-50 flex justify-between items-center">
          <Link to="/" onClick={closeMobileMenu} className="flex items-center group">
            <img 
              src={logo}
              alt="Mercuri Shop Logo" 
              className="h-8 mr-3 transition-transform duration-300 group-hover:scale-110" 
              style={{ objectFit: 'contain' }}
            />
            <div className="flex flex-col">
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                Mercuri Shop
              </span>
              <span className="text-xs text-gray-500">WhatsApp Business</span>
            </div>
          </Link>
          <Button 
            icon={<CloseOutlined />} 
            onClick={closeMobileMenu} 
            className="h-10 w-10 border-0 bg-white/80 hover:bg-white rounded-xl shadow-sm transition-all duration-200"
          />
        </div>
        
        {/* Navigation mobile moderne */}
        <div className="p-6">
          <nav className="space-y-2 mb-8">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = getSelectedKeys().includes(item.key);
              return (
                <Link 
                  key={item.key}
                  to={item.path} 
                  onClick={closeMobileMenu}
                  className={`
                    flex items-center p-4 rounded-xl transition-all duration-200 group
                    ${isActive 
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg' 
                      : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                    }
                  `}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  <span className="font-medium">{item.label}</span>
                  <ChevronRightIcon className="h-4 w-4 ml-auto opacity-50 group-hover:opacity-100 transition-opacity duration-200" />
                </Link>
              );
            })}
          </nav>
          
          {/* Actions mobiles modernes */}
          <div className="space-y-3 pt-4 border-t border-gray-200">
            <Link to="/login" onClick={closeMobileMenu}>
              <Button 
                block 
                size="large"
                className="h-12 border-2 border-gray-200 text-gray-700 hover:border-blue-300 hover:text-blue-600 rounded-xl font-medium transition-all duration-200"
              >
                Se connecter
              </Button>
            </Link>
            <Link to="/register" onClick={closeMobileMenu}>
              <Button 
                block
                size="large"
                className="h-12 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 border-0 rounded-xl text-white font-medium shadow-lg transform hover:scale-[1.02] transition-all duration-200"
              >
                S'inscrire gratuitement
              </Button>
            </Link>
          </div>
        </div>
      </Drawer>
      
      {/* Main content avec padding adapté au header plus haut */}
      <Content className="pt-20">
        {/* Afficher children si fournis, sinon utiliser Outlet pour les routes enfants */}
        {children || <Outlet />}
      </Content>
      
      {/* Footer moderne */}
      <Footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white px-4 py-12 relative overflow-hidden">
        {/* Éléments décoratifs du footer */}
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
        </div>
        
        <div className="container mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Branding section */}
            <div className="md:col-span-1">
              <div className="flex items-center mb-4">
                <img 
                  src={logo}
                  alt="Mercuri Shop Logo" 
                  className="h-8 mr-3" 
                  style={{ objectFit: 'contain' }}
                />
                <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  Mercuri Shop
                </span>
              </div>
              <p className="text-gray-300 leading-relaxed">
                La solution complète pour automatiser vos conversations WhatsApp et développer votre entreprise en Côte d'Ivoire et en Afrique.
              </p>
              {/* Statistiques attractives */}
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-white/5 rounded-lg backdrop-blur-sm">
                  <div className="text-2xl font-bold text-blue-400">500+</div>
                  <div className="text-xs text-gray-400">Entreprises</div>
                </div>
                <div className="text-center p-3 bg-white/5 rounded-lg backdrop-blur-sm">
                  <div className="text-2xl font-bold text-cyan-400">99%</div>
                  <div className="text-xs text-gray-400">Satisfaction</div>
                </div>
              </div>
            </div>
            
            {/* Navigation section */}
            <div>
              <h3 className="text-lg font-bold mb-4 text-blue-400">Navigation</h3>
              <ul className="space-y-3">
                {navItems.map(item => (
                  <li key={item.key}>
                    <Link 
                      to={item.path} 
                      className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center group"
                    >
                      <ChevronRightIcon className="h-3 w-3 mr-2 opacity-0 group-hover:opacity-100 transition-all duration-200" />
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Services section */}
            <div>
              <h3 className="text-lg font-bold mb-4 text-cyan-400">Services</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                  Automatisation WhatsApp
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mr-3"></div>
                  Gestion des commandes
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                  Point de vente (POS)
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mr-3"></div>
                  Intégration Shopify
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                  Support 24/7
                </li>
              </ul>
            </div>
            
            {/* Contact section */}
            <div>
              <h3 className="text-lg font-bold mb-4 text-blue-400">Contact</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 mr-3 mt-0.5 text-cyan-400" />
                  <div>
                    <div className="font-medium">Email</div>
                    <div>contact@mercurishop.com</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <InformationCircleIcon className="h-5 w-5 mr-3 mt-0.5 text-blue-400" />
                  <div>
                    <div className="font-medium">Téléphone</div>
                    <div>+225 07 07 07 07 07</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <HomeIcon className="h-5 w-5 mr-3 mt-0.5 text-cyan-400" />
                  <div>
                    <div className="font-medium">Adresse</div>
                    <div>Abidjan, Côte d'Ivoire</div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Divider moderne */}
          <div className="h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent my-8"></div>
          
          {/* Copyright section */}
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 mb-4 md:mb-0">
              &copy; {new Date().getFullYear()} Mercuri Shop. Tous droits réservés.
            </div>
            
            {/* Liens légaux */}
            <div className="flex space-x-6 text-sm text-gray-400">
              <Link to="/terms" className="hover:text-white transition-colors duration-200">
                Conditions d'utilisation
              </Link>
              <Link to="/privacy" className="hover:text-white transition-colors duration-200">
                Confidentialité
              </Link>
              <Link to="/cookies" className="hover:text-white transition-colors duration-200">
                Cookies
              </Link>
            </div>
          </div>
        </div>
      </Footer>
    </Layout>
  );
};

export default PublicLayout; 